import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Box,
  CheckCircle2,
  ChevronRight,
  Image as ImageIcon,
  IndianRupee,
  Layers,
  Tag,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import type {
  Attribute,
  Brand,
  Category,
  CategoryDiscount,
  Color,
  HomepageSection,
} from '@terra/api-client';
import {
  Badge,
  type BadgeTone,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  PageHeader,
  Select,
  Spinner,
} from '@terra/ui';

import { FormField, FormSelect } from '../components/form';
import { MediaLibraryPicker } from '../components/MediaLibraryPicker';
import { ProductImageUploader, type ProductImage } from '../components/ProductImageUploader';
import { api } from '../lib/api';

const formatPaise = (p: number) =>
  `₹${(p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const PRODUCT_TONE: Record<string, BadgeTone> = {
  DRAFT: 'neutral',
  PENDING_APPROVAL: 'warning',
  CHANGES_REQUESTED: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  ARCHIVED: 'neutral',
};

// ══════════════════════════════════════════════════════════════════
// Product list — reused for All / In-House / Digital / Seller variants
// ══════════════════════════════════════════════════════════════════

interface ProductFilter {
  status?: string;
  isFirstParty?: 'true' | 'false';
  productKind?: 'PHYSICAL' | 'DIGITAL';
}

function ProductsListView({
  title,
  description,
  filter,
}: {
  title: string;
  description: string;
  filter: ProductFilter;
}) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const key = ['catalog-products', filter, search];

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () =>
      api.products.listAll({
        pageSize: 100,
        ...(search ? { q: search } : {}),
        ...(filter.status ? { status: filter.status } : {}),
        ...(filter.isFirstParty ? { isFirstParty: filter.isFirstParty } : {}),
        ...(filter.productKind ? { productKind: filter.productKind } : {}),
      }),
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['catalog-products'] });
  const approve = useMutation({
    mutationFn: (id: string) => api.products.approve(id),
    onSuccess: invalidate,
  });
  const reject = useMutation({
    mutationFn: (id: string) => api.products.reject(id, { note: 'Does not meet guidelines' }),
    onSuccess: invalidate,
  });
  // A seller's homepage request ("Popular Picks"…) only takes effect once the
  // admin places it; approving the product alone never did.
  const placeAsRequested = useMutation({
    mutationFn: ({ id, section }: { id: string; section: HomepageSection }) =>
      api.products.setPlacement(id, { homepageSection: section, showOnHomepage: true }),
    onSuccess: invalidate,
  });
  const toggleVisibility = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.products.setVisibility(id, isActive),
    onSuccess: invalidate,
  });
  const toggleFeatured = useMutation({
    mutationFn: ({ id, isFeatured }: { id: string; isFeatured: boolean }) =>
      api.products.setFeatured(id, isFeatured),
    onSuccess: invalidate,
  });
  const removeProduct = useMutation({
    mutationFn: (id: string) => api.products.remove(id),
    onSuccess: invalidate,
  });

  const products = data?.data ?? [];

  return (
    <div>
      <PageHeader title={title} description={description} />
      <div className="mb-4">
        <Input
          placeholder="Search products…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-72"
        />
      </div>
      {isLoading ? (
        <div className="grid min-h-[200px] place-items-center">
          <Spinner size="lg" />
        </div>
      ) : products.length === 0 ? (
        <EmptyState title="No products" description="No products match this view yet." />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border bg-surface-subtle text-ink-muted border-b text-xs font-medium uppercase">
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Vendor</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Price</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Storefront</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr
                    key={p.id}
                    className="border-border hover:bg-surface-subtle/50 border-b last:border-0"
                  >
                    <td className="px-4 py-3">
                      <div className="text-ink font-medium">{p.title}</div>
                      <div className="text-ink-muted text-xs">{p.variants[0]?.sku ?? '—'}</div>
                    </td>
                    <td className="text-ink-muted px-4 py-3">{p.vendor?.storeName ?? '—'}</td>
                    <td className="text-ink-muted px-4 py-3">{p.category?.name ?? '—'}</td>
                    <td className="px-4 py-3 font-medium">
                      {p.variants[0] ? formatPaise(p.variants[0].pricePaise) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone={PRODUCT_TONE[p.status] ?? 'neutral'}>
                        {p.status.replace(/_/g, ' ')}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {/* What shoppers actually see: every storefront rule, not just
                            the on/off switch, which said "Live" for products a
                            disabled brand or category was hiding. */}
                        {p.storefront ? (
                          <Badge tone={p.storefront.visible ? 'success' : 'danger'} dot>
                            {p.storefront.visible ? 'On store' : 'Not on store'}
                          </Badge>
                        ) : (
                          <Badge tone={p.isActive ? 'success' : 'neutral'} dot>
                            {p.isActive ? 'Live' : 'Hidden'}
                          </Badge>
                        )}
                        {p.isFeatured && <Badge tone="warning">Featured</Badge>}
                        {p.homepageSection && p.showOnHomepage && (
                          <Badge tone="info">Home: {sectionLabel(p.homepageSection)}</Badge>
                        )}
                      </div>
                      {p.storefront && !p.storefront.visible && (
                        <ul className="mt-1 space-y-0.5 text-[11px] text-red-700">
                          {p.storefront.blockers.map((b) => (
                            <li key={b}>• {b}</li>
                          ))}
                        </ul>
                      )}
                      {p.requestedHomepageSection &&
                        !(p.homepageSection === p.requestedHomepageSection && p.showOnHomepage) && (
                          <div className="mt-1.5 flex items-center gap-2 text-[11px] text-amber-700">
                            <span>Seller asks for {sectionLabel(p.requestedHomepageSection)}</span>
                            <button
                              type="button"
                              disabled={placeAsRequested.isPending}
                              onClick={() =>
                                placeAsRequested.mutate({
                                  id: p.id,
                                  section: p.requestedHomepageSection!,
                                })
                              }
                              className="rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 font-semibold hover:bg-amber-100 disabled:opacity-50"
                            >
                              Place there
                            </button>
                          </div>
                        )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        {p.status === 'PENDING_APPROVAL' && (
                          <>
                            <Button size="sm" onClick={() => approve.mutate(p.id)}>
                              Approve
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => reject.mutate(p.id)}>
                              Reject
                            </Button>
                          </>
                        )}
                        {p.status === 'APPROVED' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                toggleVisibility.mutate({ id: p.id, isActive: !p.isActive })
                              }
                            >
                              {p.isActive ? 'Hide' : 'Show'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                toggleFeatured.mutate({ id: p.id, isFeatured: !p.isFeatured })
                              }
                            >
                              {p.isFeatured ? 'Unfeature' : 'Feature'}
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            if (
                              window.confirm(
                                `Delete "${p.title}"? It will be removed from the storefront.`,
                              )
                            )
                              removeProduct.mutate(p.id);
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

export const AllProductsPage = () => (
  <ProductsListView
    title="All Products"
    description="Every product across the marketplace and in-house catalog."
    filter={{}}
  />
);
export const InHouseProductsPage = () => (
  <ProductsListView
    title="In-House Products"
    description="First-party products owned and sold by Terra Shoppy."
    filter={{ isFirstParty: 'true' }}
  />
);
export const DigitalProductsPage = () => (
  <ProductsListView
    title="Digital Products"
    description="Downloadable / licence-based catalog items."
    filter={{ productKind: 'DIGITAL' }}
  />
);
export const SellerPhysicalPage = () => (
  <ProductsListView
    title="Seller — Physical Products"
    description="Shippable goods listed by marketplace sellers."
    filter={{ isFirstParty: 'false', productKind: 'PHYSICAL' }}
  />
);
export const SellerDigitalPage = () => (
  <ProductsListView
    title="Seller — Digital Products"
    description="Digital goods listed by marketplace sellers."
    filter={{ isFirstParty: 'false', productKind: 'DIGITAL' }}
  />
);

// ══════════════════════════════════════════════════════════════════
// Add New Product
// ══════════════════════════════════════════════════════════════════

const SECTION_LABELS: Record<HomepageSection, string> = {
  POPULAR_PICKS: 'Popular Picks',
  BOTTOM_GRID_1: 'Bottom Grid 1',
  BOTTOM_GRID_2: 'Bottom Grid 2',
  BEST_SELLING: 'Best Selling',
  FEATURED: 'Featured',
};
const sectionLabel = (s: HomepageSection) => SECTION_LABELS[s] ?? s;

export function AddProductPage() {
  const qc = useQueryClient();
  const categories = useQuery({
    queryKey: ['categories-flat'],
    queryFn: () => api.categories.list(),
  });
  const vendors = useQuery({
    queryKey: ['vendors-min'],
    queryFn: () => api.vendors.list({ pageSize: 100 }),
  });

  const [form, setForm] = useState({
    title: '',
    slug: '',
    categoryId: '',
    vendorId: '',
    brand: '',
    hsnSac: '',
    gstRate: '18',
    productKind: 'PHYSICAL' as 'PHYSICAL' | 'DIGITAL',
    mrpRupees: '',
    priceRupees: '',
    sku: '',
  });
  const [media, setMedia] = useState<ProductImage[]>([]);

  const create = useMutation({
    mutationFn: () =>
      api.catalog.bulkImportProducts(form.vendorId, [
        {
          title: form.title.trim(),
          slug: form.slug || slugify(form.title),
          categoryId: form.categoryId,
          ...(form.brand ? { brand: form.brand } : {}),
          hsnSac: form.hsnSac.trim(),
          gstRate: Number(form.gstRate),
          productKind: form.productKind,
          mrpPaise: Math.round(Number(form.mrpRupees) * 100),
          pricePaise: Math.round(Number(form.priceRupees) * 100),
          ...(form.sku ? { sku: form.sku } : {}),
          ...(media.length > 0
            ? {
                media: media.map((m) => ({
                  fileId: m.fileId,
                  url: m.url,
                  publicId: m.publicId,
                  alt: m.alt || undefined,
                  isPrimary: m.isPrimary,
                })),
              }
            : {}),
        },
      ]),
    onSuccess: () => {
      setForm({
        title: '',
        slug: '',
        categoryId: '',
        vendorId: '',
        brand: '',
        hsnSac: '',
        gstRate: '18',
        productKind: 'PHYSICAL',
        mrpRupees: '',
        priceRupees: '',
        sku: '',
      });
      setMedia([]);
      void qc.invalidateQueries({ queryKey: ['catalog-products'] });
    },
  });

  const valid =
    form.title.length >= 2 &&
    form.categoryId &&
    form.vendorId &&
    /^\d{4,8}$/.test(form.hsnSac) &&
    Number(form.mrpRupees) > 0 &&
    Number(form.priceRupees) > 0 &&
    media.length >= 1;

  const mrp = Number(form.mrpRupees);
  const price = Number(form.priceRupees);
  const discountPct =
    mrp > 0 && price > 0 && price <= mrp ? Math.round(((mrp - price) / mrp) * 100) : 0;

  const sectionMotion = (i: number) => ({
    initial: { opacity: 0, y: 14 },
    animate: { opacity: 1, y: 0 },
    transition: { delay: i * 0.07, duration: 0.4, ease: [0.16, 1, 0.3, 1] as const },
  });

  return (
    <div>
      <PageHeader
        eyebrow="Catalog"
        title="Add New Product"
        description="Create a catalog product. Admin-created products are published live to the storefront immediately."
        breadcrumbs={
          <>
            <Link to="/products/all" className="text-ink-subtle hover:text-gold-700">
              Products
            </Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-ink">Add New Product</span>
          </>
        }
      />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) create.mutate();
        }}
      >
        <div className="grid gap-6 lg:grid-cols-3">
          {/* ── Left — main content ──────────────────────────────────── */}
          <div className="space-y-6 lg:col-span-2">
            <motion.div {...sectionMotion(0)}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2.5">
                    <span className="bg-gold-50 text-gold-700 ring-gold-100 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
                      <Box className="h-4 w-4" />
                    </span>
                    Product information
                  </CardTitle>
                </CardHeader>
                <CardBody className="grid gap-5 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Input
                      label="Title"
                      placeholder="e.g. Premium Cotton T-Shirt"
                      value={form.title}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          title: e.target.value,
                          slug: f.slug || slugify(e.target.value),
                        }))
                      }
                      required
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Input
                      label="Slug"
                      value={form.slug}
                      onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                      hint="Auto-filled from the title — editable for clean URLs."
                    />
                  </div>
                </CardBody>
              </Card>
            </motion.div>

            <motion.div {...sectionMotion(1)}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2.5">
                    <span className="bg-gold-50 text-gold-700 ring-gold-100 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
                      <ImageIcon className="h-4 w-4" />
                    </span>
                    Product images
                  </CardTitle>
                </CardHeader>
                <CardBody>
                  <ProductImageUploader
                    value={media}
                    onChange={setMedia}
                    disabled={create.isPending}
                  />
                </CardBody>
              </Card>
            </motion.div>

            <motion.div {...sectionMotion(2)}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2.5">
                    <span className="bg-accent-50 text-accent-600 ring-accent-100 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
                      <Layers className="h-4 w-4" />
                    </span>
                    Classification
                  </CardTitle>
                </CardHeader>
                <CardBody className="grid gap-5 sm:grid-cols-2">
                  <Select
                    label="Vendor"
                    placeholder={vendors.isLoading ? 'Loading vendors…' : 'Select vendor…'}
                    value={form.vendorId}
                    onValueChange={(v) => setForm((f) => ({ ...f, vendorId: v }))}
                    options={(vendors.data?.data ?? []).map((v) => ({
                      value: v.id,
                      label: v.storeName,
                    }))}
                    required
                  />
                  <Select
                    label="Category"
                    placeholder={categories.isLoading ? 'Loading categories…' : 'Select category…'}
                    value={form.categoryId}
                    onValueChange={(v) => setForm((f) => ({ ...f, categoryId: v }))}
                    options={(categories.data ?? []).map((cat) => ({
                      value: cat.id,
                      label: cat.name,
                    }))}
                    required
                  />
                  <Select
                    label="Product kind"
                    value={form.productKind}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, productKind: v as 'PHYSICAL' | 'DIGITAL' }))
                    }
                    options={[
                      { value: 'PHYSICAL', label: 'Physical' },
                      { value: 'DIGITAL', label: 'Digital' },
                    ]}
                  />
                  <Input
                    label="Brand"
                    placeholder="Optional"
                    value={form.brand}
                    onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))}
                  />
                </CardBody>
              </Card>
            </motion.div>

            <motion.div {...sectionMotion(2)}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2.5">
                    <span className="grid h-8 w-8 place-items-center rounded-lg bg-red-50 text-red-600 ring-1 ring-inset ring-red-100">
                      <IndianRupee className="h-4 w-4" />
                    </span>
                    Pricing &amp; tax
                  </CardTitle>
                </CardHeader>
                <CardBody className="grid gap-5 sm:grid-cols-2">
                  <Input
                    label="HSN / SAC"
                    value={form.hsnSac}
                    onChange={(e) => setForm((f) => ({ ...f, hsnSac: e.target.value }))}
                    hint="4–8 digits"
                    required
                  />
                  <Select
                    label="GST rate"
                    value={form.gstRate}
                    onValueChange={(v) => setForm((f) => ({ ...f, gstRate: v }))}
                    options={['0', '3', '5', '12', '18', '28'].map((r) => ({
                      value: r,
                      label: `${r}%`,
                    }))}
                  />
                  <Input
                    label="MRP (₹)"
                    type="number"
                    min="0"
                    step="0.01"
                    leading={<IndianRupee className="h-4 w-4" />}
                    value={form.mrpRupees}
                    onChange={(e) => setForm((f) => ({ ...f, mrpRupees: e.target.value }))}
                    required
                  />
                  <Input
                    label="Selling price (₹)"
                    type="number"
                    min="0"
                    step="0.01"
                    leading={<IndianRupee className="h-4 w-4" />}
                    value={form.priceRupees}
                    onChange={(e) => setForm((f) => ({ ...f, priceRupees: e.target.value }))}
                    required
                  />
                  <div className="sm:col-span-2">
                    <Input
                      label="SKU"
                      value={form.sku}
                      onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))}
                      hint="Auto-generated if left blank."
                    />
                  </div>
                </CardBody>
              </Card>
            </motion.div>
          </div>

          {/* ── Right — publish sidebar ──────────────────────────────── */}
          <motion.div {...sectionMotion(1)} className="space-y-6">
            <div className="space-y-6 lg:sticky lg:top-[92px]">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2.5">
                    <span className="bg-gold-50 text-gold-700 ring-gold-100 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
                      <CheckCircle2 className="h-4 w-4" />
                    </span>
                    Publish
                  </CardTitle>
                  <Badge tone="success" dot>
                    Live
                  </Badge>
                </CardHeader>
                <CardBody className="space-y-4">
                  <p className="text-ink-muted text-sm leading-relaxed">
                    Admin-created products are published{' '}
                    <span className="text-ink font-semibold">live</span> to the storefront
                    immediately — no moderation queue.
                  </p>
                  <Button
                    type="submit"
                    size="lg"
                    fullWidth
                    loading={create.isPending}
                    disabled={!valid}
                  >
                    Create product
                  </Button>
                  {!valid && (
                    <p className="text-ink-subtle text-xs">
                      Complete title, vendor, category, HSN/SAC and pricing to enable creation.
                    </p>
                  )}
                  {create.isSuccess && create.data && (
                    <div className="border-gold-200 bg-gold-50 text-gold-800 overflow-hidden rounded-xl border px-3.5 py-3 text-sm font-medium">
                      {create.data.created} created, {create.data.skipped} skipped.
                      {create.data.errors.length > 0 && (
                        <ul className="mt-1.5 list-disc space-y-0.5 pl-4 text-xs font-normal text-red-600">
                          {create.data.errors.map((err, i) => (
                            <li key={i} className="break-words [overflow-wrap:anywhere]">
                              {err}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  {create.isError && (
                    <div className="overflow-hidden break-words rounded-xl border border-red-200 bg-red-50 px-3.5 py-3 text-sm font-medium text-red-700 [overflow-wrap:anywhere]">
                      {create.error instanceof Error
                        ? create.error.message
                        : 'Could not create product.'}
                    </div>
                  )}
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2.5">
                    <span className="bg-surface-subtle text-ink-muted ring-border grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
                      <Tag className="h-4 w-4" />
                    </span>
                    Live preview
                  </CardTitle>
                </CardHeader>
                <CardBody className="space-y-3">
                  <p className="text-ink truncate text-sm font-semibold">
                    {form.title || 'Untitled product'}
                  </p>
                  <div className="flex items-end gap-2">
                    <span className="text-ink text-2xl font-bold tracking-tighter">
                      {price > 0 ? formatPaise(Math.round(price * 100)) : '₹0.00'}
                    </span>
                    {discountPct > 0 && (
                      <>
                        <span className="text-ink-subtle pb-1 text-sm line-through">
                          {formatPaise(Math.round(mrp * 100))}
                        </span>
                        <Badge tone="red" className="mb-1">
                          -{discountPct}%
                        </Badge>
                      </>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    <Badge tone="neutral">
                      {form.productKind === 'DIGITAL' ? 'Digital' : 'Physical'}
                    </Badge>
                    {form.brand && <Badge tone="gold">{form.brand}</Badge>}
                    {form.gstRate && <Badge tone="info">GST {form.gstRate}%</Badge>}
                  </div>
                </CardBody>
              </Card>
            </div>
          </motion.div>
        </div>
      </form>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Bulk Import / Export Products
// ══════════════════════════════════════════════════════════════════

export function BulkImportProductsPage() {
  const vendors = useQuery({
    queryKey: ['vendors-min'],
    queryFn: () => api.vendors.list({ pageSize: 100 }),
  });
  const [vendorId, setVendorId] = useState('');
  const [json, setJson] = useState('');

  const importMut = useMutation({
    mutationFn: () => {
      let rows: Record<string, unknown>[];
      try {
        rows = JSON.parse(json);
        if (!Array.isArray(rows)) throw new Error('JSON must be an array');
      } catch (e) {
        throw new Error(e instanceof Error ? e.message : 'Invalid JSON');
      }
      return api.catalog.bulkImportProducts(vendorId, rows);
    },
  });

  const sample = JSON.stringify(
    [
      {
        title: 'Sample Product',
        categoryId: 'CATEGORY_ID',
        hsnSac: '6109',
        gstRate: 18,
        productKind: 'PHYSICAL',
        mrpPaise: 50000,
        pricePaise: 40000,
      },
    ],
    null,
    2,
  );

  return (
    <div>
      <PageHeader
        title="Bulk Import Products"
        description="Paste a JSON array of products to create them in one batch."
      />
      <Card className="max-w-3xl">
        <CardBody>
          <div className="space-y-3">
            <FormSelect
              label="Vendor"
              value={vendorId}
              onChange={(e) => setVendorId(e.target.value)}
            >
              <option value="">Select vendor…</option>
              {(vendors.data?.data ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.storeName}
                </option>
              ))}
            </FormSelect>
            <FormField label="Products JSON">
              <textarea
                className="border-border-strong/35 bg-surface text-ink hover:border-border-strong/60 focus:border-gold-400 focus:shadow-focus h-64 w-full rounded-xl border px-3.5 py-2.5 font-mono text-xs transition-all duration-200 focus:outline-none"
                value={json}
                onChange={(e) => setJson(e.target.value)}
                placeholder={sample}
              />
            </FormField>
            <Button
              onClick={() => importMut.mutate()}
              loading={importMut.isPending}
              disabled={!vendorId || !json.trim()}
            >
              Import products
            </Button>
            {importMut.isError && (
              <p className="text-sm text-red-600">
                {importMut.error instanceof Error ? importMut.error.message : 'Import failed'}
              </p>
            )}
            {importMut.data && (
              <p className="text-gold-700 text-sm">
                {importMut.data.created} created, {importMut.data.skipped} skipped.
                {importMut.data.errors.length > 0 && ` Errors: ${importMut.data.errors.join('; ')}`}
              </p>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

export function BulkExportProductsPage() {
  const [status, setStatus] = useState('');
  const exportMut = useMutation({
    mutationFn: () => api.catalog.exportProducts(status ? { status } : {}),
  });

  function downloadCsv() {
    const rows = exportMut.data ?? [];
    if (rows.length === 0) return;
    const headers = Object.keys(rows[0]!);
    const csv = [
      headers.join(','),
      ...rows.map((r) =>
        headers
          .map((h) => JSON.stringify((r as unknown as Record<string, unknown>)[h] ?? ''))
          .join(','),
      ),
    ].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `terra-products-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      <PageHeader
        title="Bulk Export Products"
        description="Export the catalog as JSON / downloadable CSV."
      />
      <Card>
        <CardBody>
          <div className="flex flex-wrap items-end gap-3">
            <FormSelect
              label="Status filter"
              className="w-56"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All statuses</option>
              {['DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'REJECTED', 'ARCHIVED'].map((s) => (
                <option key={s} value={s}>
                  {s.replace(/_/g, ' ')}
                </option>
              ))}
            </FormSelect>
            <Button onClick={() => exportMut.mutate()} loading={exportMut.isPending}>
              Run export
            </Button>
            {exportMut.data && exportMut.data.length > 0 && (
              <Button variant="outline" onClick={downloadCsv}>
                Download CSV ({exportMut.data.length})
              </Button>
            )}
          </div>
          {exportMut.data && (
            <div className="border-border mt-4 overflow-x-auto rounded-lg border">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border bg-surface-subtle text-ink-muted border-b text-xs font-medium uppercase">
                    <th className="px-3 py-2">Title</th>
                    <th className="px-3 py-2">Category</th>
                    <th className="px-3 py-2">Kind</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {exportMut.data.map((r) => (
                    <tr key={r.id} className="border-border border-b last:border-0">
                      <td className="px-3 py-2">{r.title}</td>
                      <td className="text-ink-muted px-3 py-2">{r.category}</td>
                      <td className="px-3 py-2">{r.productKind}</td>
                      <td className="px-3 py-2">
                        <Badge tone={PRODUCT_TONE[r.status] ?? 'neutral'}>
                          {r.status.replace(/_/g, ' ')}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">{formatPaise(r.pricePaise)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Category
// ══════════════════════════════════════════════════════════════════

export function CategoryPage() {
  const qc = useQueryClient();
  const categories = useQuery({
    queryKey: ['categories-flat'],
    queryFn: () => api.categories.list(),
  });
  const [form, setForm] = useState({
    name: '',
    slug: '',
    parentId: '',
    commissionPct: '10',
    returnDays: '',
    imageUrl: '',
    iconColor: '',
  });
  /**
   * Which category the image picker is choosing an icon for: an id, 'new' for
   * the create form, or null when closed. The icon is shown in the mobile app's
   * and storefront's category row; without one the apps pick an emoji.
   */
  const [iconFor, setIconFor] = useState<string | null>(null);
  const [iconError, setIconError] = useState<string | null>(null);

  const categoryMap = useState(() => {
    const map: Record<string, string> = {};
    return map;
  })[0];

  // Keep categoryMap updated for quick parent name resolution
  const flatData = categories.data ?? [];
  const parentLookup: Record<string, string> = {};
  flatData.forEach((c: Category) => {
    parentLookup[c.id] = c.name;
  });

  const create = useMutation({
    mutationFn: () =>
      api.categories.create({
        name: form.name.trim(),
        slug: form.slug || slugify(form.name),
        ...(form.parentId ? { parentId: form.parentId } : {}),
        commissionBps: Math.round(Number(form.commissionPct) * 100),
        ...(form.returnDays.trim() !== ''
          ? { returnWindowDays: Math.max(0, Math.round(Number(form.returnDays))) }
          : {}),
        ...(form.imageUrl ? { imageUrl: form.imageUrl } : {}),
        ...(form.iconColor ? { iconColor: form.iconColor } : {}),
      }),
    onSuccess: () => {
      setForm({
        name: '',
        slug: '',
        parentId: '',
        commissionPct: '10',
        returnDays: '',
        imageUrl: '',
        iconColor: '',
      });
      void qc.invalidateQueries({ queryKey: ['categories-flat'] });
      void qc.invalidateQueries({ queryKey: ['categories-tree'] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.categories.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['categories-flat'] });
      void qc.invalidateQueries({ queryKey: ['categories-tree'] });
    },
  });
  const setReturnWindow = useMutation({
    mutationFn: ({ id, days }: { id: string; days: number | null }) =>
      api.categories.update(id, { returnWindowDays: days }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['categories-flat'] }),
  });
  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.categories.update(id, { active }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['categories-flat'] });
      void qc.invalidateQueries({ queryKey: ['categories-tree'] });
    },
  });
  const setIcon = useMutation({
    mutationFn: ({ id, imageUrl }: { id: string; imageUrl: string | null }) =>
      api.categories.update(id, { imageUrl }),
    onSuccess: () => {
      setIconError(null);
      void qc.invalidateQueries({ queryKey: ['categories-flat'] });
      void qc.invalidateQueries({ queryKey: ['categories-tree'] });
    },
    onError: (err: any) =>
      setIconError(
        err?.response?.data?.error?.message ?? err?.message ?? 'Could not save the icon.',
      ),
  });

  const setColor = useMutation({
    mutationFn: ({ id, iconColor }: { id: string; iconColor: string | null }) =>
      api.categories.update(id, { iconColor }),
    onSuccess: () => {
      setIconError(null);
      void qc.invalidateQueries({ queryKey: ['categories-flat'] });
      void qc.invalidateQueries({ queryKey: ['categories-tree'] });
    },
    onError: (err: any) =>
      setIconError(
        err?.response?.data?.error?.message ?? err?.message ?? 'Could not save the colour.',
      ),
  });

  // Filter root categories for parent selector dropdown
  const parentOptions = flatData.filter((c: Category) => !c.parentId);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Category"
        description="Manage catalog categories, parent-child hierarchies, and default commissions."
      />
      <Card>
        <CardBody>
          <h3 className="text-ink-muted mb-3 text-sm font-semibold uppercase">New category</h3>
          <form
            className="grid gap-3 sm:grid-cols-4 lg:grid-cols-8"
            onSubmit={(e) => {
              e.preventDefault();
              if (form.name.length >= 2) create.mutate();
            }}
          >
            <Input
              label="Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Wheat or Spices"
              required
            />
            <Input
              label="Slug"
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              hint="auto from name"
            />
            <FormSelect
              label="Parent Category (Optional)"
              value={form.parentId}
              onChange={(e) => setForm((f) => ({ ...f, parentId: e.target.value }))}
            >
              <option value="">None (Main Category)</option>
              {parentOptions.map((p: Category) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </FormSelect>
            <Input
              label="Commission (%)"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={form.commissionPct}
              onChange={(e) => setForm((f) => ({ ...f, commissionPct: e.target.value }))}
            />
            <Input
              label="Return window (days)"
              type="number"
              min="0"
              max="90"
              value={form.returnDays}
              onChange={(e) => setForm((f) => ({ ...f, returnDays: e.target.value }))}
              hint="blank = inherit · 0 = not returnable"
            />
            <div>
              <span className="text-ink mb-1.5 block text-sm font-medium">Icon (optional)</span>
              <div className="flex items-center gap-2">
                <CategoryIcon
                  imageUrl={form.imageUrl || null}
                  color={form.iconColor || null}
                  onPick={() => setIconFor('new')}
                  onClear={
                    form.imageUrl ? () => setForm((f) => ({ ...f, imageUrl: '' })) : undefined
                  }
                />
                <span className="text-ink-muted text-[11px]">Square PNG, transparent</span>
              </div>
            </div>
            <div>
              <span className="text-ink mb-1.5 block text-sm font-medium">Icon colour</span>
              <IconColorPicker
                value={form.iconColor || null}
                onChange={(c) => setForm((f) => ({ ...f, iconColor: c ?? '' }))}
              />
            </div>
            <div className="flex items-end">
              <Button type="submit" loading={create.isPending}>
                Add Category
              </Button>
            </div>
          </form>
          {iconError && <p className="mt-3 text-sm font-medium text-red-600">{iconError}</p>}
        </CardBody>
      </Card>

      {categories.isLoading ? (
        <Spinner size="lg" />
      ) : flatData.length === 0 ? (
        <EmptyState title="No categories" description="Create your first category above." />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border bg-surface-subtle text-ink-muted border-b text-xs font-medium uppercase">
                  <th className="px-4 py-3">Icon / Colour</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Type / Parent</th>
                  <th className="px-4 py-3">Slug</th>
                  <th className="px-4 py-3">Commission</th>
                  <th className="px-4 py-3">Returns</th>
                  <th className="px-4 py-3">Products</th>
                  <th className="px-4 py-3">Active</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {flatData.map((cat: Category) => (
                  <tr
                    key={cat.id}
                    className="border-border border-b last:border-0 hover:bg-slate-50/50"
                  >
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <CategoryIcon
                          imageUrl={cat.imageUrl ?? null}
                          color={cat.iconColor ?? null}
                          disabled={setIcon.isPending}
                          onPick={() => setIconFor(cat.id)}
                          onClear={
                            cat.imageUrl
                              ? () => {
                                  if (window.confirm(`Remove the icon of ${cat.name}?`)) {
                                    setIcon.mutate({ id: cat.id, imageUrl: null });
                                  }
                                }
                              : undefined
                          }
                        />
                        <IconColorPicker
                          compact
                          value={cat.iconColor ?? null}
                          disabled={setColor.isPending}
                          onChange={(iconColor) => setColor.mutate({ id: cat.id, iconColor })}
                        />
                      </div>
                    </td>
                    <td
                      className={`px-4 py-3 font-medium ${cat.parentId ? 'pl-8 text-slate-700' : 'font-bold text-slate-900'}`}
                    >
                      {cat.parentId ? `↳ ${cat.name}` : cat.name}
                    </td>
                    <td className="px-4 py-3">
                      {cat.parentId ? (
                        <span className="inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
                          Sub of {parentLookup[cat.parentId] || 'Parent'}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">
                          Main Category
                        </span>
                      )}
                    </td>
                    <td className="text-ink-muted px-4 py-3 font-mono text-xs">{cat.slug}</td>
                    <td className="px-4 py-3">{(cat.commissionBps / 100).toFixed(2)}%</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        className="text-xs font-semibold underline-offset-2 hover:underline"
                        title="Change the return window"
                        onClick={() => {
                          const input = window.prompt(
                            'Return window in days (0 = not returnable, blank = inherit):',
                            cat.returnWindowDays == null ? '' : String(cat.returnWindowDays),
                          );
                          if (input === null) return;
                          const days = input.trim() === '' ? null : Number(input);
                          if (days !== null && (!Number.isInteger(days) || days < 0 || days > 90)) {
                            window.alert('Enter a whole number of days from 0 to 90.');
                            return;
                          }
                          setReturnWindow.mutate({ id: cat.id, days });
                        }}
                      >
                        {cat.returnWindowDays == null
                          ? 'Inherit'
                          : cat.returnWindowDays === 0
                            ? 'Not returnable'
                            : `${cat.returnWindowDays} days`}
                      </button>
                    </td>
                    <td className="px-4 py-3">{cat._count?.products ?? 0}</td>
                    <td className="px-4 py-3">
                      {cat.active ? (
                        <Badge tone="success">Active</Badge>
                      ) : (
                        <Badge tone="neutral">Off</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggle.mutate({ id: cat.id, active: !cat.active })}
                        >
                          {cat.active ? 'Disable' : 'Enable'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => remove.mutate(cat.id)}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <MediaLibraryPicker
        open={iconFor !== null}
        onClose={() => setIconFor(null)}
        uploadFolder="categories"
        title="Choose a category icon"
        onSelect={(file) => {
          const target = iconFor;
          setIconFor(null);
          if (!file.url) {
            setIconError('That file has no public link; upload it again.');
            return;
          }
          if (target === 'new') setForm((f) => ({ ...f, imageUrl: file.url! }));
          else if (target) setIcon.mutate({ id: target, imageUrl: file.url });
        }}
      />
    </div>
  );
}

/**
 * An image tile: the image when there is one, otherwise an "add" tile. Used for
 * category icons (tinted with the icon colour) and brand logos.
 */
function CategoryIcon({
  imageUrl,
  onPick,
  onClear,
  disabled,
  color,
  label = 'icon',
}: {
  imageUrl: string | null;
  onPick: () => void;
  onClear?: (() => void) | undefined;
  disabled?: boolean;
  color?: string | null;
  label?: string;
}) {
  return (
    <div className="relative inline-flex">
      <button
        type="button"
        onClick={onPick}
        disabled={disabled}
        title={imageUrl ? `Change ${label}` : `Add ${label}`}
        style={color ? { backgroundColor: `${color}1F`, borderColor: color } : undefined}
        className="border-border/70 hover:border-gold-400 flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl border bg-white transition-colors disabled:opacity-50"
      >
        {imageUrl ? (
          <img src={imageUrl} alt="" className="h-full w-full object-contain p-1" />
        ) : (
          <ImageIcon className="text-ink-subtle h-4 w-4" style={color ? { color } : undefined} />
        )}
      </button>
      {onClear && (
        <button
          type="button"
          onClick={onClear}
          title={`Remove ${label}`}
          className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold leading-none text-white"
        >
          ×
        </button>
      )}
    </div>
  );
}

/** Quick picks for icon colours: the brand gold and red, then a spread of hues. */
const ICON_COLOR_PRESETS = [
  '#D9A93A',
  '#D63232',
  '#EA580C',
  '#D97706',
  '#65A30D',
  '#16A34A',
  '#0D9488',
  '#0284C7',
  '#2563EB',
  '#7C3AED',
  '#DB2777',
  '#475569',
];

/**
 * Colour for a category's icon: presets, a custom colour, or "Auto" (null),
 * where the apps choose one. `compact` shows just the swatch, for table rows.
 */
function IconColorPicker({
  value,
  onChange,
  disabled,
  compact,
}: {
  value: string | null;
  onChange: (color: string | null) => void;
  disabled?: boolean;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(value ?? '#D9A93A');
  const pick = (color: string | null) => {
    setOpen(false);
    if (color !== value) onChange(color);
  };

  return (
    <div className="relative inline-flex">
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setCustom(value ?? '#D9A93A');
          setOpen((o) => !o);
        }}
        title={value ? `Icon colour ${value}` : 'Icon colour: auto'}
        className={`border-border/70 hover:border-gold-400 flex items-center gap-2 rounded-lg border bg-white transition-colors disabled:opacity-50 ${compact ? 'h-7 w-7 justify-center' : 'h-11 px-2.5'}`}
      >
        <span
          className="h-5 w-5 shrink-0 rounded-full border border-black/10"
          style={
            value
              ? { backgroundColor: value }
              : {
                  background:
                    'conic-gradient(#D63232, #D9A93A, #16A34A, #0284C7, #7C3AED, #D63232)',
                }
          }
        />
        {!compact && <span className="text-ink font-mono text-xs">{value ?? 'Auto'}</span>}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="border-border absolute left-0 top-full z-50 mt-1 w-56 rounded-xl border bg-white p-3 shadow-lg">
            <div className="grid grid-cols-6 gap-2">
              {ICON_COLOR_PRESETS.map((c) => (
                <button
                  key={c}
                  type="button"
                  title={c}
                  onClick={() => pick(c)}
                  className={`h-7 w-7 rounded-full border-2 ${value === c ? 'border-ink' : 'border-transparent'}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <input
                type="color"
                value={custom}
                onChange={(e) => setCustom(e.target.value.toUpperCase())}
                className="h-8 w-10 cursor-pointer rounded border-0 bg-transparent p-0"
                aria-label="Custom colour"
              />
              <span className="text-ink-muted flex-1 font-mono text-xs">{custom}</span>
              <Button size="sm" type="button" onClick={() => pick(custom)}>
                Use
              </Button>
            </div>
            <button
              type="button"
              onClick={() => pick(null)}
              className="text-ink-muted hover:text-ink mt-2 text-xs font-medium underline-offset-2 hover:underline"
            >
              Auto (let the apps choose)
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Category Based Discount
// ══════════════════════════════════════════════════════════════════

export function CategoryDiscountPage() {
  const qc = useQueryClient();
  const discounts = useQuery({
    queryKey: ['category-discounts'],
    queryFn: () => api.catalog.listCategoryDiscounts(),
  });
  const categories = useQuery({
    queryKey: ['categories-flat'],
    queryFn: () => api.categories.list(),
  });
  const [form, setForm] = useState({ categoryId: '', label: '', discountPct: '' });

  const create = useMutation({
    mutationFn: () =>
      api.catalog.createCategoryDiscount({
        categoryId: form.categoryId,
        label: form.label.trim(),
        discountBps: Math.round(Number(form.discountPct) * 100),
      }),
    onSuccess: () => {
      setForm({ categoryId: '', label: '', discountPct: '' });
      void qc.invalidateQueries({ queryKey: ['category-discounts'] });
    },
  });
  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.catalog.updateCategoryDiscount(id, { active }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['category-discounts'] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.catalog.deleteCategoryDiscount(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['category-discounts'] }),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Category Based Discount"
        description="Time-boxed percentage discounts applied to an entire category."
      />
      <Card>
        <CardBody>
          <h3 className="text-ink-muted mb-3 text-sm font-semibold uppercase">New discount</h3>
          <form
            className="grid gap-3 sm:grid-cols-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (form.categoryId && form.label && Number(form.discountPct) > 0) create.mutate();
            }}
          >
            <FormSelect
              label="Category"
              value={form.categoryId}
              onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}
              required
            >
              <option value="">Select…</option>
              {(categories.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </FormSelect>
            <Input
              label="Label"
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              required
            />
            <Input
              label="Discount (%)"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={form.discountPct}
              onChange={(e) => setForm((f) => ({ ...f, discountPct: e.target.value }))}
              required
            />
            <div className="flex items-end">
              <Button type="submit" loading={create.isPending}>
                Add
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      {discounts.isLoading ? (
        <Spinner size="lg" />
      ) : (discounts.data ?? []).length === 0 ? (
        <EmptyState title="No category discounts" description="Create your first discount above." />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border bg-surface-subtle text-ink-muted border-b text-xs font-medium uppercase">
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Label</th>
                  <th className="px-4 py-3">Discount</th>
                  <th className="px-4 py-3">Active</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(discounts.data ?? []).map((d: CategoryDiscount) => (
                  <tr key={d.id} className="border-border border-b last:border-0">
                    <td className="px-4 py-3 font-medium">{d.category?.name ?? '—'}</td>
                    <td className="px-4 py-3">{d.label}</td>
                    <td className="px-4 py-3 font-semibold">{(d.discountBps / 100).toFixed(2)}%</td>
                    <td className="px-4 py-3">
                      {d.active ? (
                        <Badge tone="success">Active</Badge>
                      ) : (
                        <Badge tone="neutral">Off</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggle.mutate({ id: d.id, active: !d.active })}
                        >
                          {d.active ? 'Disable' : 'Enable'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => remove.mutate(d.id)}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Brands
// ══════════════════════════════════════════════════════════════════

export function BrandsPage() {
  const qc = useQueryClient();
  const brands = useQuery({ queryKey: ['brands'], queryFn: () => api.catalog.listBrands() });
  const [form, setForm] = useState({ name: '', slug: '', description: '', logoUrl: '' });
  /** Which brand the image picker is choosing a logo for: an id, 'new', or null. */
  const [logoFor, setLogoFor] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.catalog.createBrand({
        name: form.name.trim(),
        slug: form.slug || slugify(form.name),
        ...(form.description ? { description: form.description } : {}),
        // The apps load `logoFileId` as the logo's image URL.
        ...(form.logoUrl ? { logoFileId: form.logoUrl } : {}),
      }),
    onSuccess: () => {
      setForm({ name: '', slug: '', description: '', logoUrl: '' });
      void qc.invalidateQueries({ queryKey: ['brands'] });
    },
  });
  const setLogo = useMutation({
    mutationFn: ({ id, logoUrl }: { id: string; logoUrl: string | null }) =>
      api.catalog.updateBrand(id, { logoFileId: logoUrl }),
    onSuccess: () => {
      setLogoError(null);
      void qc.invalidateQueries({ queryKey: ['brands'] });
    },
    onError: (err: any) =>
      setLogoError(
        err?.response?.data?.error?.message ?? err?.message ?? 'Could not save the logo.',
      ),
  });
  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.catalog.updateBrand(id, { active }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['brands'] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.catalog.deleteBrand(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['brands'] }),
  });

  return (
    <div className="space-y-6">
      <PageHeader title="All Brand" description="Manage the structured brand registry." />
      {(() => {
        // Brands a seller typed in while adding a product are created without
        // a logo; point the admin at them instead of leaving initials on the store.
        const missing = (brands.data ?? []).filter((b: Brand) => !b.logoFileId).length;
        return missing > 0 ? (
          <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <ImageIcon className="h-4 w-4 shrink-0" />
            <span>
              <span className="font-bold">{missing}</span>{' '}
              {missing === 1 ? 'brand has' : 'brands have'} no logo yet (often ones a seller added
              with a new product). Click the highlighted logo tile to upload one; it shows on the
              website and app right away.
            </span>
          </div>
        ) : null;
      })()}
      <Card>
        <CardBody>
          <h3 className="text-ink-muted mb-3 text-sm font-semibold uppercase">New brand</h3>
          <form
            className="grid gap-3 sm:grid-cols-5"
            onSubmit={(e) => {
              e.preventDefault();
              if (form.name.length >= 2) create.mutate();
            }}
          >
            <Input
              label="Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
            <Input
              label="Slug"
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              hint="auto from name"
            />
            <Input
              label="Description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
            <div>
              <span className="text-ink mb-1.5 block text-sm font-medium">Logo</span>
              <div className="flex items-center gap-2">
                <CategoryIcon
                  label="logo"
                  imageUrl={form.logoUrl || null}
                  onPick={() => setLogoFor('new')}
                  onClear={form.logoUrl ? () => setForm((f) => ({ ...f, logoUrl: '' })) : undefined}
                />
                <span className="text-ink-muted text-[11px]">Square PNG, transparent</span>
              </div>
            </div>
            <div className="flex items-end">
              <Button type="submit" loading={create.isPending}>
                Add
              </Button>
            </div>
          </form>
          {logoError && <p className="mt-3 text-sm font-medium text-red-600">{logoError}</p>}
        </CardBody>
      </Card>

      {brands.isLoading ? (
        <Spinner size="lg" />
      ) : (brands.data ?? []).length === 0 ? (
        <EmptyState title="No brands" description="Create your first brand above." />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border bg-surface-subtle text-ink-muted border-b text-xs font-medium uppercase">
                  <th className="px-4 py-3">Logo</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Slug</th>
                  <th className="px-4 py-3">Products</th>
                  <th className="px-4 py-3">Active</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(brands.data ?? []).map((b: Brand) => (
                  <tr
                    key={b.id}
                    className={`border-border border-b last:border-0 ${b.logoFileId ? '' : 'bg-amber-50/40'}`}
                  >
                    <td className="px-4 py-2">
                      <CategoryIcon
                        label="logo"
                        imageUrl={b.logoFileId}
                        disabled={setLogo.isPending}
                        onPick={() => setLogoFor(b.id)}
                        onClear={
                          b.logoFileId
                            ? () => {
                                if (window.confirm(`Remove the logo of ${b.name}?`)) {
                                  setLogo.mutate({ id: b.id, logoUrl: null });
                                }
                              }
                            : undefined
                        }
                      />
                    </td>
                    <td className="px-4 py-3 font-medium">{b.name}</td>
                    <td className="text-ink-muted px-4 py-3 font-mono text-xs">{b.slug}</td>
                    <td className="px-4 py-3">{b._count?.products ?? 0}</td>
                    <td className="px-4 py-3">
                      {b.active ? (
                        <Badge tone="success">Active</Badge>
                      ) : (
                        <Badge tone="neutral">Off</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggle.mutate({ id: b.id, active: !b.active })}
                        >
                          {b.active ? 'Disable' : 'Enable'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => remove.mutate(b.id)}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <MediaLibraryPicker
        open={logoFor !== null}
        onClose={() => setLogoFor(null)}
        uploadFolder="brands"
        title="Choose a brand logo"
        onSelect={(file) => {
          const target = logoFor;
          setLogoFor(null);
          if (!file.url) {
            setLogoError('That file has no public link; upload it again.');
            return;
          }
          if (target === 'new') setForm((f) => ({ ...f, logoUrl: file.url! }));
          else if (target) setLogo.mutate({ id: target, logoUrl: file.url });
        }}
      />
    </div>
  );
}

export function BulkImportBrandPage() {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const importMut = useMutation({
    mutationFn: () => {
      const brands = text
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
        .map((name) => ({ name }));
      return api.catalog.bulkImportBrands(brands);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['brands'] }),
  });

  return (
    <div>
      <PageHeader
        title="Bulk Import Brand"
        description="Paste one brand name per line to create them in a single batch."
      />
      <Card className="max-w-2xl">
        <CardBody>
          <div className="space-y-3">
            <textarea
              className="border-border bg-surface h-56 w-full rounded-md border px-3 py-2 text-sm"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={'Nike\nAdidas\nPuma'}
            />
            <Button
              onClick={() => importMut.mutate()}
              loading={importMut.isPending}
              disabled={!text.trim()}
            >
              Import brands
            </Button>
            {importMut.data && (
              <p className="text-gold-700 text-sm">
                {importMut.data.created} created, {importMut.data.skipped} skipped.
                {importMut.data.errors.length > 0 && ` Errors: ${importMut.data.errors.join('; ')}`}
              </p>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Attributes
// ══════════════════════════════════════════════════════════════════

export function AttributesPage() {
  const qc = useQueryClient();
  const attributes = useQuery({
    queryKey: ['attributes'],
    queryFn: () => api.catalog.listAttributes(),
  });
  const [form, setForm] = useState({
    name: '',
    slug: '',
    kind: 'SELECT' as 'TEXT' | 'SELECT' | 'NUMBER' | 'BOOLEAN',
  });
  const [valueInputs, setValueInputs] = useState<Record<string, string>>({});

  const create = useMutation({
    mutationFn: () =>
      api.catalog.createAttribute({
        name: form.name.trim(),
        slug: form.slug || slugify(form.name),
        kind: form.kind,
      }),
    onSuccess: () => {
      setForm({ name: '', slug: '', kind: 'SELECT' });
      void qc.invalidateQueries({ queryKey: ['attributes'] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.catalog.deleteAttribute(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['attributes'] }),
  });
  const addValue = useMutation({
    mutationFn: ({ id, value }: { id: string; value: string }) =>
      api.catalog.addAttributeValue(id, value),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['attributes'] }),
  });
  const removeValue = useMutation({
    mutationFn: (valueId: string) => api.catalog.deleteAttributeValue(valueId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['attributes'] }),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Attribute"
        description="Reusable product attributes and their allowed values."
      />
      <Card>
        <CardBody>
          <h3 className="text-ink-muted mb-3 text-sm font-semibold uppercase">New attribute</h3>
          <form
            className="grid gap-3 sm:grid-cols-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (form.name.length >= 2) create.mutate();
            }}
          >
            <Input
              label="Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
            <Input
              label="Slug"
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              hint="auto from name"
            />
            <FormSelect
              label="Kind"
              value={form.kind}
              onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value as typeof form.kind }))}
            >
              {['SELECT', 'TEXT', 'NUMBER', 'BOOLEAN'].map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </FormSelect>
            <div className="flex items-end">
              <Button type="submit" loading={create.isPending}>
                Add
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      {attributes.isLoading ? (
        <Spinner size="lg" />
      ) : (attributes.data ?? []).length === 0 ? (
        <EmptyState title="No attributes" description="Create your first attribute above." />
      ) : (
        <div className="space-y-3">
          {(attributes.data ?? []).map((attr: Attribute) => (
            <Card key={attr.id}>
              <CardBody>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span className="text-ink font-semibold">{attr.name}</span>
                    <Badge tone="neutral" className="ml-2">
                      {attr.kind}
                    </Badge>
                    <span className="text-ink-muted ml-2 font-mono text-xs">{attr.slug}</span>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => remove.mutate(attr.id)}>
                    Delete
                  </Button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {attr.values.map((v) => (
                    <span
                      key={v.id}
                      className="bg-surface-subtle inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs"
                    >
                      {v.value}
                      <button
                        type="button"
                        className="text-ink-subtle hover:text-red-600"
                        onClick={() => removeValue.mutate(v.id)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {attr.values.length === 0 && (
                    <span className="text-ink-subtle text-xs">No values yet.</span>
                  )}
                </div>
                <form
                  className="mt-3 flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const val = (valueInputs[attr.id] ?? '').trim();
                    if (val) {
                      addValue.mutate({ id: attr.id, value: val });
                      setValueInputs((m) => ({ ...m, [attr.id]: '' }));
                    }
                  }}
                >
                  <Input
                    placeholder="Add value…"
                    value={valueInputs[attr.id] ?? ''}
                    onChange={(e) => setValueInputs((m) => ({ ...m, [attr.id]: e.target.value }))}
                    className="w-48"
                  />
                  <Button size="sm" type="submit">
                    Add value
                  </Button>
                </form>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Colors
// ══════════════════════════════════════════════════════════════════

export function ColorsPage() {
  const qc = useQueryClient();
  const colors = useQuery({ queryKey: ['colors'], queryFn: () => api.catalog.listColors() });
  const [form, setForm] = useState({ name: '', hexCode: '#67BED9' });

  const create = useMutation({
    mutationFn: () => api.catalog.createColor({ name: form.name.trim(), hexCode: form.hexCode }),
    onSuccess: () => {
      setForm({ name: '', hexCode: '#67BED9' });
      void qc.invalidateQueries({ queryKey: ['colors'] });
    },
  });
  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.catalog.updateColor(id, { active }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['colors'] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.catalog.deleteColor(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['colors'] }),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Colors"
        description="Named colour swatches used for product variant options."
      />
      <Card>
        <CardBody>
          <h3 className="text-ink-muted mb-3 text-sm font-semibold uppercase">New colour</h3>
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (form.name.length >= 2) create.mutate();
            }}
          >
            <Input
              label="Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
            <FormField label="Hex">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.hexCode}
                  onChange={(e) => setForm((f) => ({ ...f, hexCode: e.target.value }))}
                  className="border-border-strong/35 h-11 w-12 cursor-pointer rounded-xl border"
                />
                <Input
                  value={form.hexCode}
                  onChange={(e) => setForm((f) => ({ ...f, hexCode: e.target.value }))}
                  className="w-32 font-mono"
                />
              </div>
            </FormField>
            <Button type="submit" loading={create.isPending}>
              Add colour
            </Button>
          </form>
        </CardBody>
      </Card>

      {colors.isLoading ? (
        <Spinner size="lg" />
      ) : (colors.data ?? []).length === 0 ? (
        <EmptyState title="No colours" description="Create your first colour swatch above." />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {(colors.data ?? []).map((c: Color) => (
            <Card key={c.id}>
              <CardBody>
                <div className="flex items-center gap-3">
                  <span
                    className="border-border h-10 w-10 rounded-lg border"
                    style={{ backgroundColor: c.hexCode }}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-ink truncate font-medium">{c.name}</p>
                    <p className="text-ink-muted font-mono text-xs">{c.hexCode}</p>
                  </div>
                  {c.active ? <Badge tone="success">On</Badge> : <Badge tone="neutral">Off</Badge>}
                </div>
                <div className="mt-3 flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggle.mutate({ id: c.id, active: !c.active })}
                  >
                    {c.active ? 'Disable' : 'Enable'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove.mutate(c.id)}>
                    Delete
                  </Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
